import { db } from "config/firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
} from "firebase/firestore";

interface GetPastMsgsParams {
  userId: string;
  receiverId: string;
  endTime: number;
  messages: any[];
  setMessages: (msgs: any[]) => void;
  setEndTime: (time: number) => void;
  setFetchedAll: (fetched: boolean) => void;
}

export const getPastMsgs = async ({
  userId,
  receiverId,
  endTime,
  messages,
  setMessages,
  setEndTime,
  setFetchedAll,
}: GetPastMsgsParams) => {
  const msgLimit = 10;

  const sentMsgsQuery = query(
    collection(db, "messages"),
    where("timeStamp", "<", endTime),
    where("senderId", "==", userId),
    where("receiverId", "==", receiverId),
    orderBy("timeStamp", "desc"),
    limit(msgLimit),
  );

  let sentMsgs: any[];
  try {
    const sentMsgsSnapshot = await getDocs(sentMsgsQuery);
    sentMsgs = [];
    sentMsgsSnapshot.forEach((doc) => sentMsgs.push(doc.data()));
  } catch (error) {
    console.log("Error getting sentMsgs: ", error);
    sentMsgs = [];
  }

  const receivedMsgsQuery = query(
    collection(db, "messages"),
    where("timeStamp", "<", endTime),
    where("receiverId", "==", userId),
    where("senderId", "==", receiverId),
    orderBy("timeStamp", "desc"),
    limit(msgLimit),
  );

  let receivedMsgs: any[];
  try {
    const receivedMsgsSnapshot = await getDocs(receivedMsgsQuery);
    receivedMsgs = [];
    receivedMsgsSnapshot.forEach((doc) => receivedMsgs.push(doc.data()));
  } catch (error) {
    console.log("Error getting receivedMsgs: ", error);
    receivedMsgs = [];
  }

  // combine messages by timestamp
  const msgs: any[] = [];
  let n = 0;
  sentMsgs.reverse();
  receivedMsgs.reverse();
  while (n < msgLimit) {
    const lastSent = sentMsgs.slice(-1)[0];
    const lastRec = receivedMsgs.slice(-1)[0];
    if (!lastSent && !lastRec) break;
    if (
      (!lastSent && lastRec) ||
      (lastRec && lastSent.timeStamp <= lastRec.timeStamp)
    ) {
      lastRec.class = "receiver";
      msgs.push(lastRec);
      receivedMsgs.pop();
    } else if (
      (!lastRec && lastSent) ||
      (lastSent && lastRec.timeStamp <= lastSent.timeStamp)
    ) {
      lastSent.class = "sender";
      msgs.push(lastSent);
      sentMsgs.pop();
    }
    n++;
  }
  msgs.reverse();
  // end fetch condition
  if (msgs.length < msgLimit) {
    setFetchedAll(true);
  }
  if (msgs.length) {
    const newEndTime = msgs[0].timeStamp;
    setEndTime(newEndTime);
    return setMessages([...msgs, ...messages]);
  }
};

interface GetFutureMsgsParams {
  userId: string;
  receiverId: string;
  cb: (msgs: any[]) => void;
}

export const getFutureMsgs = async ({
  userId,
  receiverId,
  cb,
}: GetFutureMsgsParams) => {
  const startTime = Date.now();
  let sentMsgs: any[] = [];
  let recMsgs: any[] = [];

  // sent messages
  const sentMsgsQuery = query(
    collection(db, "messages"),
    where("timeStamp", ">", startTime),
    where("senderId", "==", userId),
    where("receiverId", "==", receiverId),
  );

  await onSnapshot(sentMsgsQuery, (snapshot) => {
    sentMsgs = [];
    snapshot.docs.map((doc) => {
      const message = doc.data();
      message.class = "receiver";
      sentMsgs.push(message);
    });
  });

  // received messages
  const receivedMsgsQuery = query(
    collection(db, "messages"),
    where("timeStamp", ">", startTime),
    where("receiverId", "==", userId),
    where("senderId", "==", receiverId),
  );

  return onSnapshot(receivedMsgsQuery, (snapshot) => {
    recMsgs = [];
    snapshot.docs.map((doc) => {
      const message = doc.data();
      message.class = "sender";
      recMsgs.push(message);
    });
    const msgs = [...recMsgs, ...sentMsgs];
    msgs.sort((a, b) => a.timeStamp - b.timeStamp);
    return cb(msgs);
  });
};
